import {
  ApplicationRef,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
  APP_INITIALIZER,
} from "@angular/core";
import { NgxTurnstileModule } from "ngx-turnstile";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { enableProdMode } from "@angular/core";
import { environment } from "./environments/environment";

import { AppComponent } from "./app/app.component";
import { provideHotToastConfig } from "@ngneat/hot-toast";
import { provideAuth, getAuth, connectAuthEmulator } from "@angular/fire/auth";
import { provideFirebaseApp, initializeApp, getApp } from "@angular/fire/app";
import { CodeInputModule } from "angular-code-input";
import { TabsModule } from "ngx-bootstrap/tabs";
import { AccordionModule } from "ngx-bootstrap/accordion";
import { CarouselModule } from "ngx-bootstrap/carousel";
import { IntercomModule } from "@supy-io/ngx-intercom";
import { TypeaheadModule } from "ngx-bootstrap/typeahead";

import { TooltipModule } from "ngx-bootstrap/tooltip";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { ModalModule } from "ngx-bootstrap/modal";
import {
  provideFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
} from "@angular/fire/firestore";
import { NgxIntlTelInputModule } from "@justin-s/ngx-intl-tel-input";
import { RegistrationModule } from "./app/registration/registration.module";
import { AppRoutingModule } from "./app/app-routing.module";
import { Router, RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { providePrimeNG } from "primeng/config";
import { AuthInterceptor } from "./app/interceptors/auth.interceptor";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import {
  LocationStrategy,
  HashLocationStrategy,
  CommonModule,
} from "@angular/common";
import { MyAccountService } from "./app/dashboard/main/common/modals/my-account-modal.service";
import { ClientGuard } from "./app/guards/client.guard";
import { PendingChangesGuard } from "./app/guards/component-can-deactivate.guard";
import { AuthGuard } from "./app/guards/auth.guard";
import { ChatService } from "./app/dashboard/chat.service";
import { DashboardService } from "./app/dashboard/dashboard.service";
import { LoginService } from "./app/login/login.service";
import * as Sentry from "@sentry/angular";
import { STORAGE } from "./app/storage.service";
import clientHubPreset from "src/clientHubPreset";

import Quill from "quill";
import { AppErrorHandler } from "./app/app-error-handler";
import { clearAssetRecoveryQueryParam } from "./app/utils/asset-load-recovery";
import { shouldDropSentryEvent } from "./app/utils/sentry-event-filter";

function createAppErrorHandler(): ErrorHandler {
  return new AppErrorHandler(
    Sentry.createErrorHandler({
      showDialog: false,
    }),
  );
}

function assertQuillIsLoaded(): void {
  if (typeof Quill !== "function" || !Quill.version) {
    throw new Error("Quill failed to load during application startup");
  }
}

// Development and local E2E sessions must never send production telemetry.
if (
  ["production", "qa"].includes(environment.envName) &&
  !Reflect.has(environment, "firebaseEmulators") &&
  !Reflect.has(environment, "localRealtime")
) {
  Sentry.init({
    dsn: "https://25265704b00a849cfb1d3334f8fe6268@o4507387170390016.ingest.us.sentry.io/4507387270201344",
    environment: environment.envName,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.05, // Capture 5% of performance transactions

    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event) {
      if (shouldDropSentryEvent(event)) {
        return null;
      }

      return event;
    },
  });
}

if (environment.production) {
  enableProdMode();

  if (window) {
    window.console.log = function () {};
    window.console.warn = function () {};
  }
}

// registerLicense(
//   "ORg4AjUWIQA/Gnt2UVhhQlVCfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTX5RdEBiXX9fcHdWQWJV"
// );

assertQuillIsLoaded();

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      FormsModule,
      RouterModule,
      AppRoutingModule,
      RegistrationModule,
      NgxIntlTelInputModule,

      ModalModule,
      BsDropdownModule,
      TooltipModule,
      TypeaheadModule,
      IntercomModule.forRoot({
        appId: "wqv1cgow",
        updateOnRouterChange: false,
      }),
      CarouselModule,
      AccordionModule,
      TabsModule,
      CodeInputModule.forRoot({
        codeLength: 6,
      }),
      provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
      provideAuth(() => {
        const auth = getAuth();
        if ("firebaseEmulators" in environment) {
          connectAuthEmulator(auth, "http://127.0.0.1:9098", {
            disableWarnings: true,
          });
        }
        return auth;
      }),
      provideFirestore(() => {
        const app = getApp();
        const dbName = environment.firebaseConfig.dbName;
        const providedFirestore = initializeFirestore(app, {}, dbName);
        if ("firebaseEmulators" in environment) {
          connectFirestoreEmulator(providedFirestore, "127.0.0.1", 8088);
        }
        return providedFirestore;
      }),
      // provideDatabase(() => getDatabase(getApp(), "firestore-qa")),
    ),
    provideHotToastConfig({
      style: {
        "z-index": 99999,
        padding: "16px",
      },
    }),
    LoginService,
    DashboardService,
    ChatService,
    AuthGuard,
    PendingChangesGuard,
    ClientGuard,
    NgxTurnstileModule,
    MyAccountService,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: "googleTagManagerId",
      useValue: "GTM-WZ2RRT4",
    },
    {
      provide: ErrorHandler,
      useFactory: createAppErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    [{ provide: STORAGE, useValue: localStorage }],
    provideAnimationsAsync(),
    providePrimeNG({
      overlayOptions: {
        showTransitionOptions: "250ms cubic-bezier(0.22, 1, 0.36, 1)",
        hideTransitionOptions: "150ms cubic-bezier(0.22, 1, 0.36, 1)",
      },
      theme: {
        preset: clientHubPreset,
        options: {
          darkModeSelector: false || "none",
        },
      },
    }),
  ],
})
  .then((moduleRef) => {
    clearAssetRecoveryQueryParam();

    const applicationRef = moduleRef.injector.get(ApplicationRef);
    const componentRef = applicationRef.components[0];

    // allows to run `ng.profiler.timeChangeDetection();`
    // enableDebugTools(componentRef);
  })
  .catch((err) => window["console"].error(err));
